import React from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { SplineChartV2, useDerivedMetricByWeekIdV2 } from 'src/components/BeaconV2Components/SplineChartV2';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import TrafficConversionRateGrid from './TrafficConversionRateGrid';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { TrafficConversionByWeekTable } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Conversion/TrafficConversionByWeekTable';
import { TrafficNoDataPlaceholder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/pages/NonTopicOrTopicGroupPage';
import { useTrafficAvailability } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/hooks/useTrafficAvailability';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import convertSplineChartV2SeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/splineChartV2';
import saveAs from 'file-saver';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

/**
 * Conversion Page for the traffic side tab
 */
export default function TrafficConversionPage() {
  const primaryLegendDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonLegendDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  // Use V2 hook for proper YTD + Prior Year handling and SplineChartV2 format
  const {
    primarySeries,
    comparisonSeries,
    percentChange,
    isLoading,
    formattedPercentChange,
    formattedPrimaryTotal,
    formattedComparisonTotal
  } = useDerivedMetricByWeekIdV2({
    indexName: 'sales',
    fieldName: 'purchaseRate'
  });

  const { isProductEntity } = useProductEntity();

  const { dataAvailable } = useTrafficAvailability();
  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const mainEntityType = useMainEntityType();
  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity);
  const entityPhrase = useEntityPhrasing({ titleOnly });
  const taxonomyText = useTaxonomySubtitleText();

  const titleToUse = 'Conversion Rate';

  const getSubtitle = () => {
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the conversion rate for ${entityPhrase}`}
          {taxonomyText}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  const convertSeriesToDelimitedData = () => {
    try {
      // Convert SplineChartV2 series to CSV format
      const data = convertSplineChartV2SeriesToDelimitedData(
        primarySeries,
        comparisonSeries,
        mainTimePeriod.displayName,
        comparisonTimePeriod.displayName,
        comparisonTimePeriod.id === 'prior-period'
      );

      // Create a blob with the data and save it as a CSV file
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${titleToUse}.csv`);
    } catch (e) {
      console.error('error: ', e);
    }

    return Promise.resolve();
  };

  return (
    <BasePageContainer>
      {dataAvailable ? (
        <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.BEACON_CONVERSION_RATE.name}
            chartName={CHART_NAMES.BEACON_CONVERSION_RATE_SPLINE}
          >
            <BeaconChartWithLegend
              convertSeriesToDelimitedData={convertSeriesToDelimitedData}
              title="Conversion Rate"
              subtitle={shouldShowBeaconProTopics() ? getSubtitle() : undefined}
              includeSummaryInfo={!shouldShowBeaconProTopics()}
              loading={isLoading}
              loadingComponent={<SplineChartLoading />}
              primaryLegendProps={{
                displayName: primaryLegendDisplayName,
                metric: formattedPrimaryTotal,
                change: formattedPercentChange,
                direction: getDirection(percentChange)
              }}
              comparisonLegendProps={{
                displayName: comparisonLegendDisplayName,
                metric: formattedComparisonTotal
              }}
            >
              <SplineChartV2
                primarySeries={primarySeries}
                comparisonSeries={comparisonSeries}
                metricType={METRICTYPE.PERCENT}
              />
            </BeaconChartWithLegend>
          </ChartContextProvider>
          {isProductEntity ? <TrafficConversionByWeekTable /> : <TrafficConversionRateGrid />}
        </Flex>
      ) : (
        <TrafficNoDataPlaceholder />
      )}
    </BasePageContainer>
  );
}
